import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts"
import { ProductDistributionChartProps } from "../../../types/analytics"
import { TruncatedText } from "../../ui/TruncatedText"

const COLORS = [
  "#3b82f6",
  "#8b5cf6",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5a3c",
  "#6b7280",
  "#ec4899",
  "#84cc16",
]

export const ProductDistributionChart: React.FC<ProductDistributionChartProps> = ({
  productAnalytics,
  showOverlay = false,
}) => {
  const { t } = useTranslation()
  const chartData = useMemo(() => {
    const productTotals = productAnalytics.reduce((acc, item) => {
      const title =
        typeof item.product_title === "string" && item.product_title.trim().length > 0
          ? item.product_title
          : t("analytics.unknownProduct", "Unknown product")

      if (!acc[title]) acc[title] = 0

      acc[title] += item.vto_events
      return acc
    }, {} as Record<string, number>)

    const sortedProducts = Object.entries(productTotals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)

    return sortedProducts
  }, [productAnalytics, t])

  const totalVtoEvents = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.value, 0)
  }, [chartData])

  const bestPerformingProduct = useMemo(() => {
    return chartData.length > 0 ? chartData[0] : null
  }, [chartData])

  const activeProducts = chartData.length

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]
      const percentage = ((data.value / totalVtoEvents) * 100).toFixed(1)
      return (
        <div className="bg-gray-800 p-3 rounded-lg border border-gray-600">
          <div className="font-semibold text-white">{data.payload.name}</div>
          <div className="text-sm text-blue-400">
            {t("analytics.productDistribution.vtoEvents")}: {data.value.toLocaleString()}
          </div>
          <div className="text-sm text-gray-300">
            {t("analytics.productDistribution.share")}: {percentage}%
          </div>
        </div>
      )
    }
    return null
  }

  const CustomLegend = ({ payload }: { payload: Array<{ value: string; color: string }> }) => (
    <div className="max-h-[200px] overflow-y-auto">
      {payload?.map((entry, index) => (
        <div key={index} className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
          <span className="text-xs text-gray-300">{entry.value}</span>
        </div>
      ))}
    </div>
  )

  if (showOverlay)
    return (
      <div className="p-4 bg-gray-100 rounded min-h-[400px]">
        <h3 className="text-lg font-semibold mb-2">{t("analytics.productDistribution.loading")}</h3>
        <div className="h-[300px] bg-gray-200 rounded" />
      </div>
    )

  if (chartData.length === 0)
    return (
      <div className="p-6 bg-dark-secondary rounded-lg border border-gray-700 h-full">
        <h3 className="text-lg font-semibold text-white">
          {t("analytics.productDistribution.title")}
        </h3>
        <div className="h-[300px] flex items-center justify-center">
          <p className="text-gray-300">{t("analytics.productDistribution.noData")}</p>
        </div>
      </div>
    )

  return (
    <div className="p-4 sm:p-6 bg-dark-secondary rounded-lg border border-gray-700 h-full overflow-hidden">
      <div className="flex flex-col gap-4 h-full">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {t("analytics.productDistribution.title")}
          </h3>
          <p className="text-sm text-gray-300">{t("analytics.productDistribution.subtitle")}</p>
        </div>
        <div className="flex gap-3 sm:gap-6 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-white">
              {t("analytics.productDistribution.bestPerformer")}
            </div>
            <div className="text-lg font-bold text-white">
              <TruncatedText
                text={bestPerformingProduct?.name || "N/A"}
                maxLength={25}
                className="text-white"
              />
            </div>
            <div className="text-sm text-gray-300">
              {bestPerformingProduct?.value.toLocaleString() || 0}{" "}
              {t("analytics.productDistribution.vtoEvents")}
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold text-white">
              {t("analytics.productDistribution.activeProducts")}
            </div>
            <div className="text-lg font-bold text-white">{activeProducts}</div>
            <div className="text-sm text-gray-300">
              {t("analytics.productDistribution.withActivity")}
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold text-white">
              {t("analytics.productDistribution.totalEvents")}
            </div>
            <div className="text-lg font-bold text-white">{totalVtoEvents.toLocaleString()}</div>
            <div className="text-sm text-gray-300">
              {t("analytics.productDistribution.acrossAll")}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-[300px] sm:min-h-[350px]">
          <div className="flex flex-col sm:flex-row gap-4 h-full">
            <div className="flex-1 min-h-[250px] sm:min-h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="w-full sm:min-w-[180px] sm:max-w-[250px]">
              <div className="text-sm font-semibold mb-2 text-white">
                {t("analytics.productDistribution.topProducts")}
              </div>
              <CustomLegend
                payload={chartData.map((item, index) => ({
                  value: item.name,
                  color: COLORS[index % COLORS.length],
                }))}
              />
            </div>
          </div>
        </div>

        <div className="p-3 bg-gray-800 rounded-lg border border-gray-600">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
            <div>
              <div className="text-sm font-semibold text-white">
                {t("analytics.productDistribution.bestPerformer")}
              </div>
              <div className="text-sm text-blue-400 font-semibold">
                <TruncatedText
                  text={bestPerformingProduct?.name || "N/A"}
                  maxLength={25}
                  className="text-blue-400"
                />
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm font-semibold text-white">
                {t("analytics.productDistribution.totalEvents")}
              </div>
              <div className="text-sm font-semibold text-white">
                {totalVtoEvents.toLocaleString()}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-white">
                {t("analytics.productDistribution.activeProducts")}
              </div>
              <div className="text-sm font-semibold text-white">{activeProducts}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
